import React, { useState } from "react"
import { useMutation, useQuery } from "urql"

import PackageSessionConfirmModal, {
  PackageSessionConfirmAction
} from "../../components/pro_portal/packages/PackageSessionConfirmModal"
import { packageSessionsAccountingCopy } from "../../components/pro_portal/packages/packageSessionsAccountingCopy"
import { Button } from "../../components/shared/Buttons"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import TypographyJs from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"
import { formatPackageSessionTimestamp, formatShortDate } from "../../utils/utils"

const Typography = TypographyJs as unknown as React.FC<Record<string, unknown>>

const CLIENT_OPEN_PACKAGES_QUERY = `
  query ClientOpenPackages($clientId: ID!) {
    client(clientId: $clientId) {
      id
      openPackages {
        id
        name
        createdAt
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          startsAt
          service { name }
        }
      }
    }
  }
`

const MARK_PACKAGE_SESSION_USED_MUTATION = `
  mutation MarkPackageSessionUsed($packageId: ID!) {
    markPackageSessionUsed(packageId: $packageId) {
      result
      errors
      package {
        id
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          startsAt
          service { name }
        }
      }
    }
  }
`

const MARK_PACKAGE_FINISHED_MUTATION = `
  mutation MarkPackageFinished($packageId: ID!) {
    markPackageFinished(packageId: $packageId) {
      result
      errors
      package {
        id
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          startsAt
          service { name }
        }
      }
    }
  }
`

const UNDO_MANUALLY_MARKED_PACKAGE_SESSION_MUTATION = `
  mutation UndoManuallyMarkedPackageSession($appointmentId: ID!) {
    undoManuallyMarkedPackageSession(appointmentId: $appointmentId) {
      result
      errors
      package {
        id
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          startsAt
          service { name }
        }
      }
    }
  }
`

type ManuallyMarkedAppointment = {
  id: string
  startsAt: string
  service?: { name?: string | null } | null
}

type OpenPackage = {
  id: string
  name?: string | null
  createdAt: string
  sessionsUsed: number
  sessionsUpcoming?: number | null
  sessionsRemaining?: number | null
  numberOfSessions?: number | null
  manuallyMarkedAppointments: ManuallyMarkedAppointment[]
}

type ClientOpenPackagesProps = {
  clientId: string | number
}

const ClientOpenPackages: React.FC<ClientOpenPackagesProps> = ({ clientId }) => {
  const { showToast } = useToast()
  const [activePackageId, setActivePackageId] = useState<string | null>(null)
  const [undoingAppointmentId, setUndoingAppointmentId] = useState<string | null>(null)
  const [confirmState, setConfirmState] = useState<{
    action: PackageSessionConfirmAction
    packageId?: string
    appointmentId?: string
    packageName?: string | null
  } | null>(null)

  const [{ data, fetching }, refetchPackages] = useQuery({
    query: CLIENT_OPEN_PACKAGES_QUERY,
    variables: { clientId },
    requestPolicy: "cache-and-network"
  })

  const [, markSessionUsed] = useMutation(MARK_PACKAGE_SESSION_USED_MUTATION)
  const [, markPackageFinished] = useMutation(MARK_PACKAGE_FINISHED_MUTATION)
  const [, undoManuallyMarkedSession] = useMutation(UNDO_MANUALLY_MARKED_PACKAGE_SESSION_MUTATION)

  const openPackages = (data?.client?.openPackages as OpenPackage[] | undefined) ?? []

  const handleMutationResult = (
    payload: { result?: string; errors?: string[] } | undefined,
    error: { message?: string } | undefined,
    successMessage: string
  ) => {
    if (payload?.result === "success") {
      showToast({ content: successMessage, type: "success" })
      refetchPackages({ requestPolicy: "network-only" })
      return true
    }

    const message =
      payload?.errors?.join(", ") ||
      error?.message?.replace("[GraphQL]", "") ||
      "Something went wrong. Please try again."
    showToast({ content: message, type: "error" })
    return false
  }

  const handleMarkSessionUsed = (packageId: string) => {
    setActivePackageId(packageId)
    return markSessionUsed({ packageId }).then((res) => {
      handleMutationResult(res.data?.markPackageSessionUsed, res.error, "Session marked as used.")
      setActivePackageId(null)
    })
  }

  const handleMarkPackageFinished = (pkg: OpenPackage) => {
    setActivePackageId(pkg.id)
    return markPackageFinished({ packageId: pkg.id }).then((res) => {
      handleMutationResult(res.data?.markPackageFinished, res.error, "Package marked as finished.")
      setActivePackageId(null)
    })
  }

  const handleUndoManuallyMarkedSession = (appointmentId: string) => {
    setUndoingAppointmentId(appointmentId)
    return undoManuallyMarkedSession({ appointmentId }).then((res) => {
      handleMutationResult(
        res.data?.undoManuallyMarkedPackageSession,
        res.error,
        "Manually marked session undone."
      )
      setUndoingAppointmentId(null)
    })
  }

  const handleConfirm = async () => {
    if (!confirmState) return
    if (confirmState.action === "markOne" && confirmState.packageId) {
      await handleMarkSessionUsed(confirmState.packageId)
    } else if (confirmState.action === "markFinished" && confirmState.packageId) {
      const pkg = openPackages.find((p) => p.id === confirmState.packageId)
      if (pkg) await handleMarkPackageFinished(pkg)
    } else if (confirmState.action === "undo" && confirmState.appointmentId) {
      await handleUndoManuallyMarkedSession(confirmState.appointmentId)
    }
    setConfirmState(null)
  }

  if (fetching && openPackages.length === 0) {
    return <LoadingSpinner className="" spinnerClassName="" />
  }

  if (openPackages.length === 0) {
    return null
  }

  return (
    <div className="mb-6">
      <Typography variant="h5" className="mb-4">
        Open packages
      </Typography>
      <div className="flex flex-col gap-4">
        {openPackages.map((pkg) => {
          const sessionsRemaining = pkg.sessionsRemaining ?? 0
          const numberOfSessions = pkg.numberOfSessions ?? 0
          const packageBusy = activePackageId === pkg.id
          const anyPackageBusy = activePackageId !== null

          return (
            <div key={pkg.id} className="rounded-xl border border-gray-light bg-white p-6">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div>
                  <div className="font-bold">{pkg.name || "Package"}</div>
                  <div className="text-sm text-gray-dark">{packageSessionsAccountingCopy(pkg)}</div>
                  {pkg.createdAt && (
                    <div className="text-sm text-gray-dark">Purchased {formatShortDate(pkg.createdAt)}</div>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 sm:mt-0">
                  <Button
                    type="secondary"
                    size="small"
                    className=""
                    floatingInMobile={false}
                    style={{}}
                    disabled={anyPackageBusy || sessionsRemaining <= 0}
                    onClick={() =>
                      setConfirmState({
                        action: "markOne",
                        packageId: pkg.id,
                        packageName: pkg.name
                      })
                    }>
                    {packageBusy && !undoingAppointmentId ? "Marking…" : "Mark session used"}
                  </Button>
                  <Button
                    type="tertiary"
                    size="small"
                    className=""
                    floatingInMobile={false}
                    style={{}}
                    disabled={anyPackageBusy || sessionsRemaining <= 0}
                    onClick={() =>
                      setConfirmState({
                        action: "markFinished",
                        packageId: pkg.id,
                        packageName: pkg.name
                      })
                    }>
                    Mark package finished
                  </Button>
                </div>
              </div>

              {pkg.manuallyMarkedAppointments.length > 0 && (
                <div className="mt-4 border-t border-gray-light pt-4">
                  <div className="mb-2 text-sm font-bold">Manually marked sessions</div>
                  <div className="flex flex-col gap-2">
                    {pkg.manuallyMarkedAppointments.map((appointment) => {
                      const undoBusy = undoingAppointmentId === appointment.id
                      const serviceName = appointment.service?.name || "Session"

                      return (
                        <div
                          key={appointment.id}
                          className="flex items-center justify-between gap-4 rounded-lg bg-gray-ultralight px-4 py-2">
                          <div className="text-sm">
                            {serviceName} · {formatPackageSessionTimestamp(appointment.startsAt)}
                          </div>
                          <Button
                            type="link"
                            size="small"
                            className=""
                            floatingInMobile={false}
                            style={{}}
                            disabled={undoBusy || anyPackageBusy}
                            onClick={() =>
                              setConfirmState({
                                action: "undo",
                                appointmentId: appointment.id,
                                packageName: pkg.name
                              })
                            }>
                            {undoBusy ? "Undoing…" : "Undo"}
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <PackageSessionConfirmModal
        visible={!!confirmState}
        action={confirmState?.action ?? null}
        packageName={confirmState?.packageName}
        onClose={() => setConfirmState(null)}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default ClientOpenPackages
